"use client";

import { useState, FormEvent, useRef, ChangeEvent } from "react";
import { FiChevronDown, FiChevronUp, FiSend, FiCheckCircle, FiImage, FiTrash2 } from "react-icons/fi";
import ConfirmationMessage from "./ConfirmationMessage";

// Définition des prix approximatifs par service et type (copié depuis ServiceGrid)
const servicePrices: Record<string, Record<string, string | number>> = {
  installation: {
    "Ballon d'eau": "400€ à 1500€",
    "Douche": "Sur devis",
    "Baignoire": "Sur devis",
    "Évier": "Sur devis",
    "Toilette": "À partir de 220€",
    "Lavabo": "À partir de 150€",
    "Sanibroyeur": "À partir de 300€",
    "Chauffage": "Sur devis",
    "Autre": "Sur devis"
  },
  reparation: {
    "Ballon d'eau": "À partir de 120€",
    "Douche": "À partir de 90€",
    "Baignoire": "À partir de 90€",
    "Évier": "À partir de 80€",
    "Toilette": "À partir de 90€",
    "Lavabo": "À partir de 80€",
    "Sanibroyeur": "À partir de 110€",
    "Chauffage": "Sur devis",
    "Autre": "Sur devis"
  }
};

// Services spécifiques par type d'équipement et type d'intervention
const serviceOptions: Record<string, Record<string, Array<{name: string, price: string, description: string}>>> = {
  "Ballon d'eau": {
    installation: [
      {
        name: "Chauffe eau Vertical (15L à 300L)",
        price: "400€ à 1200€",
        description: "Fourniture de Ballon d'eau chaude de marque (Atlantic, Thermor, Ariston, Chaffoteaux, De Dietrich, Sauter)"
      },
      {
        name: "Chauffe eau Horizontal (45L à 200L)",
        price: "950€ à 1500€",
        description: "Fourniture de Ballon d'eau chaude de marque (Atlantic, Thermor, Ariston, Chaffoteaux, De Dietrich, Sauter)"
      },
      {
        name: "Autre demande (création)",
        price: "Sur devis",
        description: "Installation personnalisée selon vos besoins spécifiques"
      }
    ],
    reparation: [
      {
        name: "Remplacement de résistance",
        price: "À partir de 120€",
        description: "Remplacement de la résistance électrique défectueuse"
      },
      {
        name: "Réparation de fuite",
        price: "À partir de 90€",
        description: "Détection et réparation de fuite sur le ballon"
      },
      {
        name: "Remplacement groupe de sécurité",
        price: "À partir de 80€",
        description: "Remplacement du groupe de sécurité défectueux"
      },
      {
        name: "Détartrage",
        price: "À partir de 150€",
        description: "Détartrage complet du ballon d'eau chaude"
      }
    ]
  },
  "Douche": {
    installation: [
      {
        name: "Installation d'un receveur de douche",
        price: "Sur devis",
        description: "Chaque projet de douche est unique. Diagnostic préalable nécessaire pour évaluer l'emplacement, le type de douche (italienne ou classique), les matériaux et contraintes techniques"
      },
      {
        name: "Installation robinet de douche avec fourniture marque",
        price: "À partir de 170€",
        description: "Fourniture d'un robinet de marque (Grohe, Hansgrohe, Jacob Delafon, Ideal Standard, etc.)"
      },
      {
        name: "Installation robinet de douche marque standard",
        price: "À partir de 140€",
        description: "Fourniture et pose d'un robinet de marque standard"
      },
      {
        name: "Pose d'un robinet fourni par le client",
        price: "À partir de 90€",
        description: "Installation d'un robinet de douche que vous fournissez"
      },
      {
        name: "Réfection du joint de silicone",
        price: "À partir de 140€",
        description: "Fourniture et pose d'un joint de silicone au contour de la douche"
      }
    ],
    reparation: [
      {
        name: "Réparation fuite robinetterie",
        price: "À partir de 90€",
        description: "Réparation ou remplacement de robinetterie qui fuit"
      },
      {
        name: "Débouchage évacuation",
        price: "À partir de 80€",
        description: "Débouchage de l'évacuation de la douche"
      },
      {
        name: "Remplacement flexible/pommeau",
        price: "À partir de 60€",
        description: "Remplacement du flexible ou de la pomme de douche"
      }
    ]
  },
  // Les autres services sont définis de la même manière que dans ServiceList.tsx
};

// Numéro de téléphone pour les tests - utiliser variable d'environnement côté serveur
const RECIPIENT_PHONE = process.env.NEXT_PUBLIC_PLOMBIER_PHONE || "07 82 96 15 20";

interface ChatFormProps {
  serviceType: 'installation' | 'reparation';
  selectedService: string;
  serviceOption: string;
  selectedDate: Date | null;
  selectedTime: string | null;
  onClose: () => void;
  isMultipleServices?: boolean;
  multipleServices?: Array<{
    type: 'installation' | 'reparation';
    service: string;
    option: string;
  }>;
}

export default function ChatForm({ 
  serviceType, 
  selectedService, 
  serviceOption,
  selectedDate,
  selectedTime,
  onClose,
  isMultipleServices,
  multipleServices
}: ChatFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [prestationAddress, setPrestationAddress] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [sameAddress, setSameAddress] = useState(true);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Message de base qui sera ajouté automatiquement lors de l'envoi
  const baseMessage = isMultipleServices 
    ? `Je souhaite prendre rendez-vous pour plusieurs services : ${multipleServices?.map(s => `${s.type === 'installation' ? 'Installation' : 'Réparation'} - ${s.service} - ${s.option}`).join(', ')}
Date souhaitée: ${selectedDate ? selectedDate.toLocaleDateString('fr-FR') : 'Non précisée'}
Heure souhaitée: ${selectedTime || 'Non précisée'}
Adresse de prestation: ${prestationAddress || 'Non précisée'}
Adresse de facturation: ${sameAddress ? 'Identique à l\'adresse de prestation' : (billingAddress || 'Non précisée')}

`
    : `Je souhaite prendre rendez-vous pour ${serviceType === 'installation' ? 'une installation' : 'une réparation'} : ${selectedService} - ${serviceOption}
Date souhaitée: ${selectedDate ? selectedDate.toLocaleDateString('fr-FR') : 'Non précisée'}
Heure souhaitée: ${selectedTime || 'Non précisée'}
Adresse de prestation: ${prestationAddress || 'Non précisée'}
Adresse de facturation: ${sameAddress ? 'Identique à l\'adresse de prestation' : (billingAddress || 'Non précisée')}

`;

  // Gérer la copie de l'adresse
  const handleSameAddressChange = (checked: boolean) => {
    setSameAddress(checked);
    if (checked) {
      setBillingAddress("");
    }
  };

  // Gérer l'upload d'image
  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFilesArray = Array.from(files);
      
      const validFiles = newFilesArray.filter(file => {
        if (!file.type.startsWith('image/')) {
          alert(`Le fichier ${file.name} n'est pas une image valide`);
          return false;
        }
        if (file.size > 8 * 1024 * 1024) {
          alert(`L'image ${file.name} dépasse la limite de 8MB`);
          return false;
        }
        return true;
      });
      
      if (validFiles.length === 0) return;
      
      setImages(prevImages => [...prevImages, ...validFiles]);
      
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prevPreviews => [...prevPreviews, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setImages(prevImages => prevImages.filter((_, i) => i !== index));
    setImagePreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!name || !phone || !prestationAddress || !message) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setIsSubmitting(true);

    try {
      const fullMessage = baseMessage + message;
      
      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email || '');
      formData.append('phone', phone);
      formData.append('prestationAddress', prestationAddress);
      formData.append('billingAddress', sameAddress ? prestationAddress : billingAddress);
      formData.append('message', fullMessage);
      formData.append('serviceType', serviceType);
      formData.append('service', selectedService);
      formData.append('serviceOption', serviceOption);
      formData.append('date', selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '');
      formData.append('time', selectedTime || '');
      
      if (images.length > 0) {
        images.forEach((image) => {
          formData.append('images', image);
        });
      }

      const response = await fetch("/api/appointments/request", {
        method: "POST",
        body: formData,
      });

      const responseData = await response.json();
      
      if (response.ok) {
        setIsSuccess(true);
        setName("");
        setEmail("");
        setPhone("");
        setPrestationAddress("");
        setBillingAddress("");
        setSameAddress(true);
        setMessage("");
        setImages([]);
        setImagePreviews([]);
      } else {
        // Gestion spécifique des erreurs de conflit (HTTP 409)
        if (response.status === 409) {
          let conflictMessage = "⚠️ Conflit de réservation détecté !\n\n";
          
          if (responseData.conflictDetails?.existingAppointment) {
            conflictMessage += `Un rendez-vous existe déjà à ce créneau :\n`;
            conflictMessage += `🕐 ${responseData.conflictDetails.existingAppointment.time}\n`;
            conflictMessage += `🔧 ${responseData.conflictDetails.existingAppointment.service}\n\n`;
            conflictMessage += `Veuillez choisir un autre créneau.`;
          } else if (responseData.capacityInfo) {
            const info = responseData.capacityInfo;
            conflictMessage += `Capacité ${info.period} dépassée :\n`;
            conflictMessage += `📊 ${info.currentPoints}/${info.maxPoints} points utilisés\n`;
            conflictMessage += `🔧 ${info.requestedPoints} points demandés\n\n`;
            conflictMessage += `Veuillez choisir un autre créneau ou une autre date.`;
          } else if (responseData.blockedSlot) {
            conflictMessage += `Ce créneau a été bloqué manuellement.\n\n`;
            conflictMessage += `Veuillez choisir un autre horaire.`;
          } else {
            conflictMessage += responseData.error || "Créneau indisponible.";
          }
          
          alert(conflictMessage);
        } else {
          // Autres erreurs (400, 500, etc.)
          alert(`Une erreur est survenue lors de l'envoi: ${responseData.error || 'Veuillez réessayer'}`);
        }
      }
    } catch (error) {
      alert(`Une erreur technique est survenue. Veuillez réessayer.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Si la demande a été soumise avec succès, afficher la confirmation
  if (isSuccess) {
    return (
      <ConfirmationMessage
        serviceType={serviceType}
        selectedService={selectedService}
        serviceOption={serviceOption}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        onClose={onClose}
        isMultipleServices={isMultipleServices}
        multipleServices={multipleServices}
      />
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 max-w-2xl mx-auto">
      {/* Détails du service */}
      <div className="mb-4">
        <div 
          className="flex justify-between items-center cursor-pointer p-3 bg-gray-50 hover:bg-gray-100 rounded-md"
          onClick={() => setShowDetails(!showDetails)}
        >
          <h3 className="font-medium">Détails de votre demande</h3>
          {showDetails ? <FiChevronUp /> : <FiChevronDown />}
        </div>
        
        {showDetails && (
          <div className="mt-3 p-4 bg-gray-50 rounded-md">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="text-gray-600">Type de service:</div>
              <div>{isMultipleServices ? "Services multiples" : (serviceType === 'installation' ? 'Installation' : 'Réparation')}</div>
              <div className="text-gray-600">Service:</div>
              <div>{selectedService}</div>
              <div className="text-gray-600">Option:</div>
              <div>{serviceOption}</div>
              <div className="text-gray-600">Date souhaitée:</div>
              <div>{selectedDate ? selectedDate.toLocaleDateString('fr-FR') : 'Non précisée'}</div>
              <div className="text-gray-600">Horaire souhaité:</div>
              <div>{selectedTime || 'Non précisé'}</div>
            </div>
          </div>
        )}
      </div>
      
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Nom *
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Votre nom"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre email"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Téléphone *
          </label>
          <input
            type="tel"
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Votre numéro de téléphone"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div>
          <label htmlFor="prestationAddress" className="block text-sm font-medium text-gray-700 mb-1">
            Adresse de prestation *
          </label>
          <input
            type="text"
            id="prestationAddress"
            value={prestationAddress}
            onChange={(e) => setPrestationAddress(e.target.value)}
            placeholder="Rue, Ville, Code Postal"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={sameAddress}
              onChange={(e) => handleSameAddressChange(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Identique à l'adresse de prestation</span>
          </label>
        </div>
        
        {!sameAddress && (
          <div>
            <label htmlFor="billingAddress" className="block text-sm font-medium text-gray-700 mb-1">
              Adresse de facturation
            </label>
            <input
              type="text"
              id="billingAddress"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              placeholder="Rue, Ville, Code Postal"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        )}
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Décrivez votre problème ou votre besoin *
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Décrivez précisément votre problème, vos besoins ou vos attentes concernant cette intervention..."
            required
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        {/* Champ d'upload d'image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Photos du problème
          </label>
          <div className="mt-1 flex items-center">
            <label htmlFor="image-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 flex items-center">
              <FiImage className="mr-2" />
              Ajouter des photos
            </label>
            <input
              id="image-upload"
              name="images"
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              onChange={handleImageChange}
              className="sr-only"
            />
          </div>
          
          {imagePreviews.length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative">
                  <img 
                    src={preview} 
                    alt={`Aperçu ${index + 1}`} 
                    className="h-32 w-full object-cover rounded-md" 
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-sm hover:bg-red-600"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {imagePreviews.length === 0 && (
            <p className="mt-1 text-xs text-gray-500">Des photos sont recommandées mais non obligatoires</p>
          )}
        </div>
        
        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Retour
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-secondary disabled:opacity-50"
          >
            {isSubmitting ? (
              <><span className="animate-spin mr-2">⟳</span> Envoi...</>
            ) : (
              <><FiSend className="mr-2" /> Envoyer</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}